// Frontend mirror of src/permissions/managed-role-registry.ts. Used for static
// UI metadata (labels, tier descriptions, single-tier detection). Keep in sync
// with the backend registry — adding a role on the backend without mirroring
// here breaks the management UI for that role.
//
// The backend is the authority for *authorization*. This file only describes
// roles for rendering.

export interface ManagedRoleTier {
  id: string;
  label: string;
  description: string;
  routeGroup: string;
}

export interface ManagedRole {
  id: string;
  label: string;
  description: string;
  tiers: ManagedRoleTier[];
  // Mirrors the backend: admins need an explicit grant for this role.
  adminsNeedGrant?: boolean;
}

export const MANAGED_ROLES: ManagedRole[] = [
  {
    id: 'client-communications',
    label: 'Client Communications',
    description:
      'Who can read what has been sent to clients, and who can draft, approve and send an operational notice.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can read campaigns, their audiences and the per-client delivery log. This is the Client Delivery Director view, and it is also what a named reviewer needs to do their review.',
        routeGroup: 'client-communications-viewers',
      },
      {
        id: 'manager',
        label: 'Sender',
        description:
          'Can draft campaigns, build audiences, approve a send and put it on the schedule. Approving still requires being somebody other than the campaign’s author.',
        routeGroup: 'client-communications-managers',
      },
    ],
  },
  {
    id: 'a2a',
    label: 'A2A Access',
    description: 'Who can view or manage A2A configurations.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view configs and analytics, cannot make changes.',
        routeGroup: 'a2a-viewers',
      },
      {
        id: 'manager',
        label: 'Editor',
        description: 'Full access: create, edit, delete configs and rotate keys.',
        routeGroup: 'a2a-managers',
      },
    ],
  },
  {
    id: 'analytics',
    label: 'AI Chat Analytics Access',
    description: 'Who can view AI Chat analytics and curate the viewer list.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view /admin/chat/analytics (metrics, conversations, feedback).',
        routeGroup: 'analytics-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'Viewer access plus ability to grant or revoke viewer access for others.',
        routeGroup: 'analytics-managers',
      },
    ],
  },
  {
    id: 'support-deflection-trace',
    label: 'Support AI Trace Access',
    description:
      'Who can inspect what each stage of the support AI deflection pipeline saw, did and decided.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can view deflection traces: stage prompts, raw model responses, evidence, and verdicts.',
        routeGroup: 'support-deflection-trace-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'Viewer access plus ability to grant or revoke viewer access for others.',
        routeGroup: 'support-deflection-trace-managers',
      },
    ],
  },
  {
    id: 'support-ps-approver',
    label: 'Support PS Approver',
    description:
      'Who can approve or reject the AI-drafted updates that go out to a client under Kibo’s name.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can see the queue of drafts waiting for approval, but cannot send or reject one.',
        routeGroup: 'support-ps-approver-viewers',
      },
      {
        id: 'manager',
        label: 'Approver',
        description:
          'Can edit, approve and reject drafted client updates, and grant or revoke that ability for others.',
        routeGroup: 'support-ps-approver-managers',
      },
    ],
  },
  {
    id: 'support-agent',
    label: 'AI Support Agent',
    description:
      'Who can see and configure the AI Support Agent: the support process and triage agent config, the case queue, the Support Lab setup and who support emails go to.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can read the support configuration and its version history, the case queue and ticket detail, automation runs, desk status, the Support Lab setup and the notification recipients. Cannot change anything.',
        routeGroup: 'support-agent-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Viewer access plus changing the support configuration, acting on tickets (reclassify, set environment, file to Jira, reset), the Support Lab persona domain and notification recipients, and granting or revoking Viewer access. The wiring — the Kibo AI Bot user, the HubSpot actor, API keys, the Support Lab key and the desk switch — stays admin-only.',
        routeGroup: 'support-agent-managers',
      },
    ],
  },
  {
    id: 'google-meets',
    label: 'Google Meet Access',
    description: 'Who can start Google Meet meetings and read the transcripts they produce.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view meetings, what happened to them, and their transcripts.',
        routeGroup: 'google-meets-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'Viewer access plus starting meetings and ending them early.',
        routeGroup: 'google-meets-managers',
      },
    ],
  },
  {
    id: 'account-modification',
    label: 'Account Modification',
    description:
      'Who can change the client database — accounts, their status, their delivery pod, and the bulk import/export.',
    tiers: [
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Can create and edit accounts, record status changes, assign delivery pods, and run the account import/export.',
        routeGroup: 'account-modification-managers',
      },
    ],
  },
  {
    id: 'pricing-calculator-manager',
    label: 'Pricing Calculator Manager',
    description: 'Who can edit the pricing calculator configuration.',
    tiers: [
      {
        id: 'manager',
        label: 'Manager',
        description: 'Can edit all pricing calculator configuration (global settings, price lists, logic, dropship, conversational, import).',
        routeGroup: 'pricing-config-managers',
      },
    ],
  },
  {
    id: 'quote-access',
    label: 'Quote Access',
    description: 'Who can view all pricing quotes, not just their own.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view all quotes across users.',
        routeGroup: 'quote-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'View all quotes, plus grant or revoke Viewer access for others.',
        routeGroup: 'quote-managers',
      },
    ],
  },
  {
    id: 'outbound-mail',
    label: 'Mail Outbox Access',
    description:
      'Who can read the Mail Outbox: every email Hub sent or captured, with its full body and recipients.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can read the Mail Outbox and its mode. Changing how mail is sent, test sends and clearing captured mail stay admin-only.',
        routeGroup: 'outbound-mail-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'Viewer access plus ability to grant or revoke viewer access for others.',
        routeGroup: 'outbound-mail-managers',
      },
    ],
  },
  {
    id: 'fireworks-proxy',
    label: 'Fireworks Proxy Manager',
    description: 'Who can view usage and manage budgets + the approved-model list for the Kibo AI Gateway.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view gateway usage, budgets, and the approved-model list.',
        routeGroup: 'fireworks-proxy-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description: 'Set the global default budget + per-user overrides, and edit the approved-model pricing list.',
        routeGroup: 'fireworks-proxy-managers',
      },
    ],
  },
  {
    id: 'grafana-triage',
    label: 'Grafana Alert Triage Access',
    description: 'Who can view triaged Grafana alerts and their AI traces, or configure the pipeline.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description: 'Can view the triaged-alert list and per-alert AI traces at /apps/grafana-triage.',
        routeGroup: 'grafana-triage-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Viewer access plus editing the triage prompt and models at /admin/grafana-triage, and granting or revoking access for others. Wiring (credential, channels, bot user) stays admin-only.',
        routeGroup: 'grafana-triage-managers',
      },
    ],
  },
  {
    id: 'article-publisher',
    label: 'Article Publisher',
    description: 'Who can publish AI chat answers to the marketing WordPress site as draft posts.',
    tiers: [
      {
        id: 'manager',
        label: 'Publisher',
        description:
          'Can turn an AI chat answer into a cleaned-up article and push it to the marketing site as a draft post.',
        routeGroup: 'article-publishers',
      },
    ],
  },
  {
    id: 'company-enrichment',
    label: 'Company Enrichment',
    description:
      'Who can run company-enrichment batches, approve HubSpot sync, edit prompts, and run reference tests.',
    tiers: [
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Can create/run enrichment batches, approve grade sync to HubSpot, manage prompts, and run reference tests.',
        routeGroup: 'company-enrichment-managers',
      },
      {
        id: 'creator',
        label: 'Creator',
        description:
          'Everything a Manager can do, plus creating HubSpot company records from the enrichment UI (the per-row “Add to HubSpot” button). Grant to the small set of people allowed to mint new HubSpot companies.',
        routeGroup: 'company-enrichment-creators',
      },
    ],
  },
  {
    id: 'sales-intelligence',
    label: 'Sales Intelligence',
    description:
      'Who can view the Sales Intelligence tile and the /apps/sales-intelligence surface.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can view the Sales Intelligence tile and the /apps/sales-intelligence surface.',
        routeGroup: 'sales-intelligence-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Everything a Viewer can see, plus the messaging alignment knowledge base and the grading rubric config. For sales leaders grading and tuning the rubric.',
        routeGroup: 'sales-intelligence-managers',
      },
    ],
  },
  {
    id: 'linkedin-post-generator',
    label: 'LinkedIn Post Generator',
    description: 'Who can use the LinkedIn Post Generator to draft and post Kibo content through their personal LinkedIn.',
    tiers: [
      {
        id: 'user',
        label: 'User',
        description:
          'Can use the LinkedIn Post Generator tile and the /apps/linkedin-post-generator surface.',
        routeGroup: 'linkedin-post-generator-users',
      },
      {
        id: 'manager',
        label: 'Manager',
        description:
          'User access plus the usage view at /admin/linkedin-post-generator (who is generating posts and how often), and granting or revoking User access for others. Config (prompts, tones, knowledge base) stays admin-only.',
        routeGroup: 'linkedin-post-generator-managers',
      },
    ],
  },
  {
    id: 'api-explorer',
    label: 'API Explorer',
    description:
      'Who can use the API Explorer to browse the Kibo endpoint catalog and call a tenant through it.',
    tiers: [
      {
        id: 'user',
        label: 'User',
        description:
          'Can use the API Explorer tile and the /apps/api-explorer surface, including calling a tenant. Reach into any tenant is still bounded by the user’s own Kibo access — delegated mode needs their SSO token and app mode needs access to the credential set — so this tier governs who gets the tool, not what the tool can reach.',
        routeGroup: 'api-explorer-users',
      },
    ],
  },
  {
    id: 'site-editor',
    label: 'Site Editor',
    description:
      'Who can change the marketing website from the Site Editor, and who can publish it',
    tiers: [
      {
        id: 'editor',
        label: 'Editor',
        description:
          'Can create drafts, edit the site by chatting with the agent and editing on the preview, send drafts to staging, and undo changes on staging. Cannot publish: nothing an editor does reaches the live site.',
        routeGroup: 'site-editor-editors',
      },
      {
        id: 'publisher',
        label: 'Publisher',
        description:
          'Everything an editor can do, plus choosing which staged changes go live, publishing a release, rolling back the last publish and unpublishing a change. Publishers can grant and revoke the Editor tier.',
        routeGroup: 'site-editor-publishers',
      },
    ],
  },
  {
    id: 'workspace-chat',
    label: 'V2 Beta Tester',
    description:
      'Who sees the V2 toggle on the new-chat screen and can use Chat V2 while it is in beta',
    tiers: [
      {
        id: 'user',
        label: 'Beta tester',
        description:
          'Can start Chat V2 threads — with no repository, on public repositories, or on org repositories the Kibo Hub GitHub App is installed on — run shell commands in them, and open pull requests. The agent commits as this person and spends their AI gateway budget.',
        routeGroup: 'workspace-chat-users',
      },
    ],
    // A beta, not a permission: admins opt in like everyone else.
    adminsNeedGrant: true,
  },
  {
    id: 'deployment-visibility',
    label: 'Ticket Deployment Visibility',
    description:
      'Who can use the Ticket Deployment Visibility tool to see what shipped / rolled back per Sandbox and TP and which Jira tickets each deploy involved.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can use the Ticket Deployment Visibility tile and the /apps/deployment-visibility surface. Minting the Jenkins webhook keys at /admin/ticket-deployments is the separate ticket-deployment-keys role.',
        routeGroup: 'deployment-visibility-viewers',
      },
    ],
  },
  {
    id: 'ticket-deployment-keys',
    label: 'Ticket Deployment Keys',
    description:
      'Who can open /admin/ticket-deployments and mint or revoke the API keys Jenkins uses to post deploy and rollback events. Members of the DevOps group pass without a grant.',
    tiers: [
      {
        id: 'manager',
        label: 'Key manager',
        description:
          'Can list, mint and revoke Ticket Deployment webhook API keys. A minted key can post deployment events, so it is a write credential into the deployment ledger.',
        routeGroup: 'ticket-deployment-key-managers',
      },
    ],
  },
  {
    id: 'client-hub-classifier',
    label: 'Implementation Portal Classifier',
    description:
      'Who can resolve quarantined Implementation Portal artefacts and assign classifications. FR-0.10: resolving a quarantine is a named human decision, recorded on the audit trail with its basis.',
    tiers: [
      {
        id: 'classifier',
        label: 'Classifier',
        description:
          'Can classify artefacts (Open / Internal / Restricted) and resolve quarantined uploads, recording the basis for each decision.',
        routeGroup: 'client-hub-classifiers',
      },
    ],
  },
  {
    id: 'security-watch',
    label: 'Security Watch',
    description:
      'Who can read Security Watch findings, which carry production client IPs, opaque user ids and AppKeys, and who can triage them and change its configuration.',
    tiers: [
      {
        id: 'viewer',
        label: 'Viewer',
        description:
          'Can read days, findings, evidence, runs, probe health and suppressions, and can be picked as an alert and digest recipient.',
        routeGroup: 'security-watch-viewers',
      },
      {
        id: 'manager',
        label: 'Manager',
        description:
          'Viewer access plus: set a finding’s status, propose and approve suppressions, edit recipients, lanes, models and thresholds, and grant viewer access.',
        routeGroup: 'security-watch-managers',
      },
    ],
    // Being a Hub admin is not being on the security data path.
    adminsNeedGrant: true,
  },
];

export function getManagedRole(id: string): ManagedRole | undefined {
  return MANAGED_ROLES.find((r) => r.id === id);
}
